/**
 * Real-time Order Status Audio Notification Engine for Aduppankarai Customer Portal
 * Plays pleasant audio chimes when restaurant staff updates order status.
 */

let audioCtx = null;

const getAudioContext = () => {
  if (typeof window === "undefined") return null;
  if (!audioCtx) {
    const AudioContextClass =
      window.AudioContext || window.webkitAudioContext;
    if (AudioContextClass) {
      audioCtx = new AudioContextClass();
    }
  }
  if (audioCtx && audioCtx.state === "suspended") {
    audioCtx.resume().catch(() => {});
  }
  return audioCtx;
};

// Auto-unlock on first customer interaction
if (typeof window !== "undefined") {
  const unlockAudio = () => {
    const ctx = getAudioContext();
    if (ctx && ctx.state === "suspended") {
      ctx.resume().catch(() => {});
    }
    window.removeEventListener("click", unlockAudio);
    window.removeEventListener("touchstart", unlockAudio);
  };
  window.addEventListener("click", unlockAudio, { passive: true });
  window.addEventListener("touchstart", unlockAudio, { passive: true });
}

export const playStatusChime = (status) => {
  const normStatus = String(status || "").toUpperCase();

  switch (normStatus) {
    case "PREPARING":
      // Soft gentle dual chime: 523.25 Hz (C5) -> 659.25 Hz (E5)
      playNotes([
        { freq: 523.25, offset: 0, duration: 0.5, gain: 0.35 },
        { freq: 659.25, offset: 0.15, duration: 0.7, gain: 0.4 },
      ]);
      break;

    case "READY":
      // Cheerful 3-tone chime: 587.33 Hz (D5) -> 739.99 Hz (F#5) -> 880 Hz (A5)
      playNotes([
        { freq: 587.33, offset: 0, duration: 0.35, gain: 0.35 },
        { freq: 739.99, offset: 0.12, duration: 0.45, gain: 0.4 },
        { freq: 880.0, offset: 0.25, duration: 0.85, gain: 0.45 },
      ]);
      break;

    case "SERVED":
      // Warm confirmation chime: 659.25 Hz (E5) -> 523.25 Hz (C5)
      playNotes([
        { freq: 659.25, offset: 0, duration: 0.4, gain: 0.3 },
        { freq: 523.25, offset: 0.15, duration: 0.6, gain: 0.35 },
      ]);
      break;

    case "COMPLETED":
      // Success victory chime: 523.25 Hz -> 659.25 Hz -> 783.99 Hz -> 1046.50 Hz
      playNotes([
        { freq: 523.25, offset: 0, duration: 0.25, gain: 0.3 },
        { freq: 659.25, offset: 0.1, duration: 0.3, gain: 0.35 },
        { freq: 783.99, offset: 0.2, duration: 0.35, gain: 0.4 },
        { freq: 1046.5, offset: 0.32, duration: 0.8, gain: 0.45 },
      ]);
      break;

    default:
      // Generic chime
      playNotes([
        { freq: 659.25, offset: 0, duration: 0.35, gain: 0.3 },
        { freq: 880.0, offset: 0.12, duration: 0.5, gain: 0.35 },
      ]);
      break;
  }
};

const playNotes = (notes) => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;

    const play = () => {
      const now = ctx.currentTime;
      notes.forEach(({ freq, offset, duration, gain: targetGain }) => {
        const osc = ctx.createOscillator();
        const gainNode = ctx.createGain();

        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, now + offset);

        gainNode.gain.setValueAtTime(0.0001, now + offset);
        gainNode.gain.exponentialRampToValueAtTime(targetGain, now + offset + 0.02);
        gainNode.gain.exponentialRampToValueAtTime(0.0001, now + offset + duration);

        osc.connect(gainNode);
        gainNode.connect(ctx.destination);

        osc.start(now + offset);
        osc.stop(now + offset + duration + 0.05);
      });
    };

    if (ctx.state === "suspended") {
      ctx.resume().then(play).catch(() => {});
    } else {
      play();
    }
  } catch (e) {
    console.warn("Customer audio chime error:", e);
  }
};
